import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTokenState } from '@/store/localStorage/useTokenState';

import { useSendMessageMutation } from '@/api/messagesApi';
import { Footer, HeaderGeneral, ServiceBackground } from '@/components/layout';
import { EntryPageProvider, useEntryPageContext } from '@/scenes/Blog/BlogEntryPage/EntryPageContext';

import { ColumnContainer } from '@/components/layout/ColumnContainer';
import { ConversationHeader } from './ConversationHeader';
import css from './ConversationPage.module.css';
import { DateSeparator } from './DateSeparator';
import { Message } from './RetroMessage';
import { useConversationMessages } from './useConversationMessages';
import { readMessagesNotifications } from '@/store/notifications/operations/readMessagesNotifications';
import { AppDispatch } from '@/app/store';
import { MessageEditForm } from './MessageEditForm';
import Pagination from '@/components/DybrComponents/DybrPagination';

export function ConversationPage() {
  const dispatch = useDispatch<AppDispatch>();
  const [token] = useTokenState();
  const { insertReply, handleCommentEdit } = useEntryPageContext();
  const { page, pageSize, messageGroups, totalMessages, activeProfileId, targetProfileId } = useConversationMessages();

  const [sendMessage] = useSendMessageMutation();

  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [newMessageId, setNewMessageId] = useState<string | null>(null);
  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<string | null>(null);

  const newMessageIds = useMemo(
    () =>
      messageGroups.flatMap(group =>
        group.messages.filter(message => message.status === 'new').map(message => message.id)
      ),
    [messageGroups]
  );

  const readNotifications = useCallback(
    (messageIds: string[]) => {
      if (messageIds.length > 0 && activeProfileId) {
        dispatch(
          readMessagesNotifications({
            messageIds,
            token,
            activeProfileId
          })
        );
      }
    },
    [dispatch, token, activeProfileId]
  );

  useEffect(() => {
    if (newMessageIds.length > 0) {
      readNotifications(newMessageIds);
    }
  }, [newMessageIds, readNotifications]);

  useEffect(() => {
    if (newMessageId) {
      const timer = setTimeout(() => {
        setNewMessageId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [newMessageId]);

  const handleCreateMessage = useCallback(
    async (content: string) => {
      if (!activeProfileId) {
        return { error: 'Profile not found' };
      }

      try {
        const result = await sendMessage({
          content,
          ownProfileId: activeProfileId,
          targetProfileId: targetProfileId || ''
        }).unwrap();

        setNewMessageId(result.id);

        return { data: result };
      } catch (error) {
        console.error('Failed to send message:', error);
        return { error: error instanceof Error ? error.message : 'Failed to send message' };
      }
    },
    [sendMessage, activeProfileId, targetProfileId]
  );

  const navigateToMessage = useCallback(
    (messageId: string) => {
      const lastPage = Math.ceil((totalMessages + 1) / pageSize);
      return `/direct/${targetProfileId}?page=${lastPage}#${messageId}`;
    },
    [totalMessages, pageSize, targetProfileId]
  );

  const handleReply = useCallback((messageId: string, profileId: string, nickname: string) => {
    // TODO: Implement reply functionality
    insertReply(messageId, profileId, nickname);
    console.log('Reply to message:', messageId);
  }, []);

  const handleEdit = useCallback((messageId: string, content: string) => {
    // TODO: Implement edit functionality
    handleCommentEdit(content);
    console.log('Edit message:', messageId, 'with content:', content);
  }, []);

  const handleDelete = useCallback((messageId: string) => {
    // TODO: Implement delete functionality
    console.log('Delete message:', messageId);
  }, []);

  return (
    <>
      <ServiceBackground data-testid="feed-scenes">
        {/* <HeaderGeneral title="разговоры" size="sm" /> */}
        <ConversationHeader size="md" />
        <div ref={topRef}>
          <ColumnContainer size="md" className={css.columnContainer}>
            <div className={css.messagesListContainer}>
              <div className={css.messagesList}>
                {messageGroups.map(group => {
                  const firstMessage = group.messages[0];
                  const hasNewMessage = group.messages.some(m => m.id === newMessageId);
                  const groupScrollTarget = group.messages.find(m => m.id === scrollTargetMessageId)?.id ?? null;

                  return (
                    <div key={firstMessage.id}>
                      {group.showDateSeparator && <DateSeparator date={firstMessage.createdAt} />}
                      <div
                        className={hasNewMessage ? 'new-message-highlight' : ''}
                        style={
                          hasNewMessage
                            ? {
                                animation: 'highlightFadeIn 2s ease-out',
                                borderRadius: '4px'
                              }
                            : undefined
                        }
                      >
                        <Message
                          messages={group.messages}
                          profile={firstMessage.sender}
                          isOwn={firstMessage.sender.id === activeProfileId}
                          scrollIntoViewId={groupScrollTarget}
                          onScrollIntoView={() => setScrollTargetMessageId(null)}
                          onReply={handleReply}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            </div>
            <Pagination
              classModifier="bottom"
              currentPage={page}
              pageSize={pageSize}
              totalRecords={totalMessages}
              reverse={true}
              className={css.paginationWrapper}
            />
            {activeProfileId && (
              <MessageEditForm
                editingComment={null}
                commentDraft={null}
                onPublishFinished={async () => {
                  // No action needed - mutation handles cache invalidation
                }}
                onUpdateComment={(_content: string) => {}}
                onCreateComment={handleCreateMessage}
                onSaveDraftComment={(_content: string) => {}}
                navigateTo={navigateToMessage}
                sendButtonLabel="отправить сообщение"
              />
            )}
            {!activeProfileId && <div className="blog-info-block">Войдите, чтобы отправить сообщение.</div>}
          </ColumnContainer>
        </div>
      </ServiceBackground>
      <Footer />
    </>
  );
}

export function ConversationPageWrapper() {
  return (
    <EntryPageProvider>
      <ConversationPage />
    </EntryPageProvider>
  );
}
